// Authentication caching and rate limiting utilities
// Prevents "too many requests" errors with Google Sign-In

interface SignInAttempt {
  timestamp: number;
  count: number;
}

interface CachedSession {
  userId: string;
  timestamp: number;
  expiresAt: number;
}

interface CachedPhoto {
  photoUrl: string;
  timestamp: number;
  expiresAt: number;
}

const RATE_LIMIT_WINDOW = 60000; // 1 minute
const MAX_ATTEMPTS_PER_WINDOW = 3; // Max 3 sign-in attempts per minute
const SESSION_CACHE_DURATION = 3600000; // 1 hour cache for sessions
const PHOTO_CACHE_DURATION = 86400000; // 24 hours cache for photos

// In-memory storage for rate limiting
const signInAttempts = new Map<string, SignInAttempt>();

// Session cache to reduce repeated auth checks
const sessionCache = new Map<string, CachedSession>();

// Photo cache to reduce repeated photo fetches
const photoCache = new Map<string, CachedPhoto>();

/**
 * Check if user is rate-limited for sign-in attempts
 * @returns true if rate limit is exceeded, false if allowed
 */
export function isRateLimited(): boolean {
  const clientId = getClientIdentifier();
  const now = Date.now();
  const attempt = signInAttempts.get(clientId);

  if (!attempt) {
    return false;
  }

  // Reset counter if window has passed
  if (now - attempt.timestamp > RATE_LIMIT_WINDOW) {
    signInAttempts.delete(clientId);
    return false;
  }

  // Check if exceeded max attempts
  return attempt.count >= MAX_ATTEMPTS_PER_WINDOW;
}

/**
 * Record a sign-in attempt for rate limiting
 */
export function recordSignInAttempt(): void {
  const clientId = getClientIdentifier();
  const now = Date.now();
  const attempt = signInAttempts.get(clientId);

  if (!attempt || now - attempt.timestamp > RATE_LIMIT_WINDOW) {
    // Start new window
    signInAttempts.set(clientId, {
      timestamp: now,
      count: 1,
    });
  } else {
    // Increment within current window
    attempt.count += 1;
    attempt.timestamp = now;
  }
}

/**
 * Get time remaining until rate limit resets
 * @returns milliseconds until rate limit resets, or 0 if not limited
 */
export function getRateLimitResetTime(): number {
  const clientId = getClientIdentifier();
  const attempt = signInAttempts.get(clientId);

  if (!attempt) {
    return 0;
  }

  const now = Date.now();
  const windowEnd = attempt.timestamp + RATE_LIMIT_WINDOW;
  const remaining = Math.max(0, windowEnd - now);

  return remaining;
}

/**
 * Cache a user session to reduce repeated auth checks
 */
export function cacheSession(userId: string): void {
  const now = Date.now();
  sessionCache.set(userId, {
    userId,
    timestamp: now,
    expiresAt: now + SESSION_CACHE_DURATION,
  });
}

/**
 * Get cached session if still valid
 * @returns userId if cached and valid, null otherwise
 */
export function getCachedSession(userId: string): string | null {
  const cached = sessionCache.get(userId);

  if (!cached) {
    return null;
  }

  const now = Date.now();
  if (now > cached.expiresAt) {
    sessionCache.delete(userId);
    return null;
  }

  return cached.userId;
}

/**
 * Clear session cache for a user
 */
export function clearSessionCache(userId: string): void {
  sessionCache.delete(userId);
}

/**
 * Clear all rate limiting and cache data
 */
export function clearAllAuthCache(): void {
  signInAttempts.clear();
  sessionCache.clear();
  photoCache.clear();
}

/**
 * Cache a user's photo URL
 */
export function cachePhoto(userId: string, photoUrl: string): void {
  if (!photoUrl) {
    return;
  }

  const now = Date.now();
  photoCache.set(userId, {
    photoUrl,
    timestamp: now,
    expiresAt: now + PHOTO_CACHE_DURATION,
  });

  console.log(`[Auth Cache] Photo cached for user ${userId.substring(0, 8)}...`);
}

/**
 * Get cached photo URL if still valid
 * @returns photoUrl if cached and valid, null otherwise
 */
export function getCachedPhoto(userId: string): string | null {
  const cached = photoCache.get(userId);

  if (!cached) {
    return null;
  }

  const now = Date.now();
  if (now > cached.expiresAt) {
    photoCache.delete(userId);
    console.log(`[Auth Cache] Photo cache expired for user ${userId.substring(0, 8)}...`);
    return null;
  }

  console.log(`[Auth Cache] Using cached photo for user ${userId.substring(0, 8)}...`);
  return cached.photoUrl;
}

/**
 * Clear photo cache for a user
 */
export function clearPhotoCache(userId: string): void {
  photoCache.delete(userId);
  console.log(`[Auth Cache] Photo cache cleared for user ${userId.substring(0, 8)}...`);
}

/**
 * Update cached photo if different from current cache
 */
export function updateCachedPhoto(userId: string, newPhotoUrl: string | null): void {
  if (!newPhotoUrl) {
    clearPhotoCache(userId);
    return;
  }

  const cached = photoCache.get(userId);
  
  // Only update if photo URL changed
  if (!cached || cached.photoUrl !== newPhotoUrl) {
    cachePhoto(userId, newPhotoUrl);
  }
}

/**
 * Get a unique client identifier for rate limiting
 * Uses a combination of factors to identify the client
 */
function getClientIdentifier(): string {
  // In browser environment, create a fingerprint based on available data
  if (typeof window !== 'undefined') {
    const factors = [
      window.navigator.userAgent,
      window.screen.width,
      window.screen.height,
      window.screen.colorDepth,
      new Date().getTimezoneOffset(),
    ];
    
    // Create a simple hash of the factors
    const fingerprint = factors.join('|');
    return btoa(fingerprint).substring(0, 32);
  }

  return 'server';
}

/**
 * Format remaining time for user-friendly display
 */
export function formatRateLimitTime(milliseconds: number): string {
  const seconds = Math.ceil(milliseconds / 1000);
  
  if (seconds <= 0) {
    return 'now';
  }
  
  if (seconds < 60) {
    return `${seconds} second${seconds !== 1 ? 's' : ''}`;
  }
  
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
}
